/**
 * Настройки в шапке: одна кнопка вместо ряда постоянных панелей (ТЗ FR-EN-83).
 *
 * До этого шапка занимала две строки: название с подзаголовком, переходы,
 * язык тренировки и пять кнопок внешнего вида. Всё это стояло на каждом
 * экране — и на экране занятия тоже, отодвигая текст упражнения за нижний
 * край. Настройки меняют раз в несколько занятий, а текст читают каждую
 * секунду; постоянное место на экране заслуживает второе, а не первое.
 *
 * Открывается диалогом поверх содержимого, а не раздвигает его (ТЗ UI-34):
 * раскрытие, сдвигающее текст упражнения вниз, — это тот же лишний ряд,
 * только появляющийся неожиданно. Ловушка фокуса, Escape и возврат фокуса
 * живут в `SettingsDialog`; здесь — кнопка, содержимое и то, что диалог
 * закрывает.
 */

import { useEffect, useRef, useState } from 'react';
import type { Translate } from '@type2x/i18n';
import type { LanguageCode, ThemePreference } from '@type2x/shared-contracts';
import { Button } from '@type2x/ui';

import { AppearanceBar } from './AppearanceBar.js';
import { LanguageBar } from './LanguageBar.js';
import { SettingsDialog } from './SettingsDialog.js';

/** Идентификатор диалога: кнопка ссылается на него `aria-controls`. */
const PANEL_ID = 'settings-panel';
/** Идентификатор заголовка: диалог называется им (`aria-labelledby`). */
const TITLE_ID = 'settings-title';

export interface SettingsPanelProps {
  readonly t: Translate;
  readonly theme: ThemePreference;
  readonly soundOnError: boolean;
  readonly gameElements: boolean;
  /** Язык тренировки; `null` — пространство ещё не загружено, менять нечего. */
  readonly trainingLanguage: LanguageCode | null;
  readonly onThemeChange: (theme: ThemePreference) => void;
  readonly onSoundOnErrorChange: (soundOnError: boolean) => void;
  readonly onGameElementsChange: (gameElements: boolean) => void;
  readonly onTrainingLanguageChange: (language: LanguageCode) => void;
}

export function SettingsPanel(props: SettingsPanelProps): React.JSX.Element {
  const { t, trainingLanguage } = props;
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  // Нажатие мимо диалога закрывает его: то, что нельзя убрать тем же
  // движением, каким открыли, остаётся висеть над текстом упражнения.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent): void => {
      const node = event.target;
      if (node instanceof Node && container.current?.contains(node) === true) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  /**
   * Закрытие возвращает фокус кнопке (ТЗ UI-34).
   *
   * Иначе фокус остаётся на исчезнувшем элементе и уезжает в начало
   * страницы: человек, работающий с клавиатуры, теряет место.
   */
  const close = (): void => {
    setOpen(false);
    trigger.current?.focus();
  };

  return (
    <div className="t2x-settings" ref={container}>
      <Button
        ref={trigger}
        variant="ghost"
        aria-expanded={open}
        aria-controls={PANEL_ID}
        aria-label={t('settings.title')}
        onClick={() => setOpen(!open)}
      >
        <span className="t2x-header__label">{t('settings.title')}</span>
      </Button>

      {open && (
        <div className="t2x-settings__panel" id={PANEL_ID}>
          <SettingsDialog t={t} titleId={TITLE_ID} onClose={close}>
            {/* Язык тренировки — первая строка диалога: его меняют чаще темы,
                и из-за него сюда заходят. До загрузки пространства менять
                нечего. */}
            {trainingLanguage !== null && (
              <LanguageBar
                t={t}
                language={trainingLanguage}
                // Выбор языка уводит на другой экран, поэтому диалог за собой
                // закрывается: оставленный поверх нового экрана, он прячет
                // то, куда человек попал. Тема, звук и награды экран не
                // меняют и диалог не закрывают — их перебирают подряд.
                onLanguageChange={(language) => {
                  setOpen(false);
                  props.onTrainingLanguageChange(language);
                }}
              />
            )}

            <AppearanceBar
              t={t}
              theme={props.theme}
              soundOnError={props.soundOnError}
              gameElements={props.gameElements}
              onThemeChange={props.onThemeChange}
              onSoundOnErrorChange={props.onSoundOnErrorChange}
              onGameElementsChange={props.onGameElementsChange}
            />
          </SettingsDialog>
        </div>
      )}
    </div>
  );
}
